/**
 * Iter 99 — Workforce Health Dashboard for Managers.
 *
 * The manager's operational command centre. Read-only. Composes existing
 * data into one screen. All corrective actions deep-link to existing
 * modules (Desktop Activity / Attendance Snapshots / Regularization /
 * Employee Profile) — this page never writes anything.
 *
 * Layout (top-to-bottom):
 *   §A  Attention Required  (FIRST — the manager must see exceptions)
 *   §B  Team Availability   (7 tile counts)
 *   §C  Team Status Grid    (operational core + 4 quick-action drill-downs)
 *   §D  Team Totals Rail    (productivity aggregates, right rail)
 *
 * Privacy invariant: This dashboard surfaces ONLY active/idle/locked
 * minutes + heartbeat timestamps + the existing attendance snapshot
 * fields. No screenshots, keystrokes, clipboard, browser history, app
 * names or window titles — ever.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Pulse, Warning, Clock, MoonStars, Coffee, Lock, WifiSlash,
    Calendar, Briefcase, ArrowSquareOut, ArrowsClockwise,
    User, ChartBar, FileText, MonitorPlay, ShieldCheck,
    Users, UserSwitch,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { formatDate, formatDateTime } from "../../lib/format";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const POLL_INTERVAL_MS = 30_000;

// Iter 99 status → badge classes (sync with backend classifier).
const STATUS_TONE = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
    idle: "bg-amber-100 text-amber-800 border-amber-200",
    locked: "bg-rose-100 text-rose-800 border-rose-200",
    offline: "bg-slate-200 text-slate-700 border-slate-300",
    not_started: "bg-slate-100 text-slate-600 border-slate-200",
    off_today: "bg-sky-100 text-sky-800 border-sky-200",
    not_tracked: "bg-slate-50 text-slate-500 border-slate-200",
};

const STATUS_LABEL = {
    active: "Working",
    idle: "Idle",
    locked: "Locked",
    offline: "Offline",
    not_started: "Not Started",
    off_today: "Off Today",
    not_tracked: "Not Tracked",
};

const SOURCE_TONE = {
    desktop_agent_bridge: "bg-violet-100 text-violet-800",
    manual_fallback: "bg-amber-100 text-amber-800",
    web: "bg-sky-100 text-sky-800",
    mobile_web: "bg-sky-100 text-sky-800",
    biometric: "bg-emerald-100 text-emerald-800",
    admin_correction: "bg-rose-100 text-rose-800",
    regularization: "bg-indigo-100 text-indigo-800",
    desktop_agent: "bg-violet-100 text-violet-800",
    hybrid: "bg-fuchsia-100 text-fuchsia-800",
    manual: "bg-sky-100 text-sky-800",
};

const SOURCE_LABEL = {
    desktop_agent_bridge: "Desktop Agent",
    manual_fallback: "Manual Fallback",
    web: "Manual ESS",
    mobile_web: "Manual ESS",
    biometric: "Biometric",
    admin_correction: "Admin",
    regularization: "Regularization",
    desktop_agent: "Desktop Agent",
    hybrid: "Hybrid",
    manual: "Manual ESS",
};

const PROD_TONE = {
    green: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
};

const EXCEPTION_BUCKET_META = {
    late_login: { label: "Late Login", icon: Clock, tone: "bg-orange-50 border-orange-200 text-orange-900" },
    long_idle: { label: "Long Idle", icon: Coffee, tone: "bg-amber-50 border-amber-200 text-amber-900" },
    agent_offline: { label: "Agent Offline", icon: WifiSlash, tone: "bg-rose-50 border-rose-200 text-rose-900" },
    pending_regularization: { label: "Pending Regularization", icon: FileText, tone: "bg-indigo-50 border-indigo-200 text-indigo-900" },
    attendance_missing: { label: "Attendance Missing", icon: Warning, tone: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900" },
};

const formatMinutes = (m) => {
    if (m === null || m === undefined) return "—";
    const total = Math.round(m);
    const h = Math.floor(total / 60);
    const r = total % 60;
    return h > 0 ? `${h}h ${r}m` : `${r}m`;
};

const formatRelative = (iso) => {
    if (!iso) return "—";
    const t = new Date(iso);
    const diffMin = Math.floor((Date.now() - t.getTime()) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin} min ago`;
    const h = Math.floor(diffMin / 60);
    if (h < 24) return `${h}h ago`;
    // Iter 102.2 · Attendance Timezone Cleanup — was `t.toLocaleString()`
    // which relied on the browser locale. Now IST-forced via `formatDateTime`.
    return formatDateTime(iso);
};

const today = () => new Date().toISOString().slice(0, 10);

export default function ManagerHealthDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [searchQ, setSearchQ] = useState("");
    const [secondsToRefresh, setSecondsToRefresh] = useState(POLL_INTERVAL_MS / 1000);
    const timerRef = useRef(null);
    // Iter 99.2 — workforce admins can view any team via ?as_manager=<emp_id>.
    // When the param is present, the dashboard renders that manager's team.
    // When omitted AND the caller has no personal team, the backend returns
    // mode="admin_picker" and the FE shows a manager picker.
    const [searchParams, setSearchParams] = useSearchParams();
    const asManager = searchParams.get("as_manager") || "";

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams({ as_of_date: today() });
            if (asManager) params.set("as_manager", asManager);
            const res = await api.get(`/workforce/manager-health/dashboard?${params.toString()}`);
            setData(res.data);
            setError(null);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || "Failed to load dashboard");
        } finally {
            setLoading(false);
            setSecondsToRefresh(POLL_INTERVAL_MS / 1000);
        }
    }, [asManager]);

    // Initial + poll. Re-mounts when asManager changes (because `load` ref changes).
    useEffect(() => {
        load();
        timerRef.current = setInterval(load, POLL_INTERVAL_MS);
        return () => clearInterval(timerRef.current);
    }, [load]);

    // Countdown ticker (1s).
    useEffect(() => {
        const t = setInterval(() => setSecondsToRefresh((s) => (s > 0 ? s - 1 : s)), 1000);
        return () => clearInterval(t);
    }, []);

    const filteredTeam = useMemo(() => {
        if (!data) return [];
        let team = data.team;
        if (statusFilter) team = team.filter((r) => r.current_status === statusFilter);
        if (searchQ.trim()) {
            const q = searchQ.trim().toLowerCase();
            team = team.filter(
                (r) =>
                    (r.employee_name || "").toLowerCase().includes(q) ||
                    (r.employee_code || "").toLowerCase().includes(q),
            );
        }
        return team;
    }, [data, statusFilter, searchQ]);

    if (loading) {
        return (
            <div className="p-8 text-center text-slate-500" data-testid="mh-loading">
                Loading Workforce Health…
            </div>
        );
    }

    if (error) {
        const friendlyNoLink = String(error).includes("No Employee profile is linked");
        return (
            <div className="p-8" data-testid="mh-error">
                <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3">
                    <strong>Cannot open Workforce Health Dashboard:</strong> {String(error)}
                    {String(error).includes("not a reporting manager") && (
                        <div className="text-xs mt-1 text-rose-700">
                            This dashboard is for users who have direct reports assigned to them
                            (via the employee&apos;s <code>reporting_manager_employee_id</code>).
                        </div>
                    )}
                    {friendlyNoLink && (
                        <div className="text-xs mt-1 text-rose-700" data-testid="mh-error-no-link-hint">
                            Ask your administrator to onboard you as an Employee and link this user
                            account to that record. Once linked, refresh this page.
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Iter 99.2 — admin picker mode: caller is a workforce admin with no
    // personal team. Render the manager picker instead of the team view.
    if (data?.mode === "admin_picker") {
        return (
            <AdminPickerView
                data={data}
                onPick={(empId) => {
                    setSearchParams({ as_manager: empId });
                }}
                secondsToRefresh={secondsToRefresh}
                onRefresh={load}
            />
        );
    }

    const s = data.live_summary;
    const ps = data.productivity_summary;
    const exceptionCount = Object.values(data.exceptions || {}).reduce((a, v) => a + v.length, 0);

    return (
        <div className="p-4 sm:p-6 max-w-[1920px] mx-auto" data-testid="mh-root">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <Pulse size={22} weight="duotone" className="text-violet-700" />
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Workforce Health</h1>
                        <Badge className="bg-violet-100 text-violet-800 text-[10px]">Manager</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                        Welcome, <strong>{data.scope.manager_name}</strong> · {new Date(data.scope.date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {data.scope.direct_reports} direct reports
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {asManager && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSearchParams({})}
                            data-testid="mh-switch-manager-btn"
                            title="Back to manager picker"
                        >
                            <UserSwitch size={14} className="mr-1.5" /> Switch manager
                        </Button>
                    )}
                    <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="mh-refresh-countdown">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                        <span className="text-slate-400">·</span>
                        <span>refreshing in {secondsToRefresh}s</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={load} data-testid="mh-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
                    </Button>
                </div>
            </div>

            {/* §A — ATTENTION REQUIRED (first thing the manager sees) */}
            <section className="mb-4" data-testid="mh-attention">
                <SectionHeader
                    icon={Warning}
                    title="Attention Required"
                    subtitle={`${exceptionCount} item${exceptionCount === 1 ? "" : "s"} need your attention today`}
                    tone="rose"
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {Object.entries(EXCEPTION_BUCKET_META).map(([code, meta]) => {
                        const items = (data.exceptions && data.exceptions[code]) || [];
                        return (
                            <ExceptionBucket
                                key={code}
                                code={code}
                                label={meta.label}
                                icon={meta.icon}
                                tone={meta.tone}
                                items={items}
                            />
                        );
                    })}
                </div>
            </section>

            {/* §B — TEAM AVAILABILITY */}
            <section className="mb-4" data-testid="mh-availability">
                <SectionHeader icon={Pulse} title="Team Availability" subtitle="Real-time presence from the Desktop Agent" tone="violet" />
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                    <AvailTile label="Total" value={s.total} tone="slate" icon={User} testid="avail-total" onClick={() => setStatusFilter(null)} active={statusFilter === null} />
                    <AvailTile label="Working" value={s.working} tone="emerald" icon={Briefcase} testid="avail-working" onClick={() => setStatusFilter("active")} active={statusFilter === "active"} />
                    <AvailTile label="Idle / Break" value={s.idle} tone="amber" icon={Coffee} testid="avail-idle" onClick={() => setStatusFilter("idle")} active={statusFilter === "idle"} />
                    <AvailTile label="Locked" value={s.locked} tone="rose" icon={Lock} testid="avail-locked" onClick={() => setStatusFilter("locked")} active={statusFilter === "locked"} />
                    <AvailTile label="Offline" value={s.offline} tone="slate" icon={WifiSlash} testid="avail-offline" onClick={() => setStatusFilter("offline")} active={statusFilter === "offline"} />
                    <AvailTile label="On Leave" value={s.on_leave} tone="sky" icon={MoonStars} testid="avail-on-leave" onClick={() => setStatusFilter("off_today")} active={statusFilter === "off_today"} />
                    <AvailTile label="Holiday" value={s.holiday} tone="sky" icon={Calendar} testid="avail-holiday" onClick={() => setStatusFilter("off_today")} active={statusFilter === "off_today"} />
                </div>
            </section>

            {/* §C + §D split */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
                {/* §C — TEAM STATUS GRID */}
                <section data-testid="mh-grid">
                    <SectionHeader icon={Pulse} title="Team Status" subtitle="Sorted with exceptions first" tone="slate">
                        <Input
                            value={searchQ}
                            onChange={(e) => setSearchQ(e.target.value)}
                            placeholder="Filter by name or code…"
                            className="h-8 text-xs w-48"
                            data-testid="mh-search"
                        />
                    </SectionHeader>
                    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                        <table className="w-full text-sm min-w-[1200px]">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Employee</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                    <th className="text-left px-3 py-2 font-medium">Last Seen</th>
                                    <th className="text-right px-3 py-2 font-medium" title="Today's active / idle / locked (minutes)">A / I / L</th>
                                    <th className="text-right px-3 py-2 font-medium">Net</th>
                                    <th className="text-left px-3 py-2 font-medium">Attendance</th>
                                    <th className="text-left px-3 py-2 font-medium">Source</th>
                                    <th className="text-center px-3 py-2 font-medium" title="Indicative metric — net working ÷ shift full-day. Does NOT affect attendance or payroll.">Productivity %</th>
                                    <th className="text-left px-3 py-2 font-medium">Quick</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTeam.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="text-center text-slate-400 py-6">No employees match the current filter.</td>
                                    </tr>
                                )}
                                {filteredTeam.map((r) => (
                                    <TeamRow key={r.employee_id} row={r} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* §D — TEAM TOTALS RAIL */}
                <aside className="space-y-3" data-testid="mh-totals">
                    <SectionHeader icon={ChartBar} title="Team Totals · Today" subtitle="Indicative aggregates" tone="indigo" />
                    <TotalsCard label="Active Hours" value={(ps.team_active_hours || 0).toFixed(1)} suffix="h" tone="emerald" testid="totals-active" />
                    <TotalsCard label="Idle Hours" value={(ps.team_idle_hours || 0).toFixed(1)} suffix="h" tone="amber" testid="totals-idle" />
                    <TotalsCard label="Locked Hours" value={(ps.team_locked_hours || 0).toFixed(1)} suffix="h" tone="rose" testid="totals-locked" />
                    <TotalsCard label="Net Working Hours" value={(ps.team_net_working_hours || 0).toFixed(1)} suffix="h" tone="violet" testid="totals-net" />
                    <TotalsCard label="Avg / Employee" value={(ps.avg_working_hours_per_employee || 0).toFixed(1)} suffix="h" tone="slate" testid="totals-avg" />
                    <TotalsCard label="Attendance %" value={(ps.attendance_pct_today ?? 0).toFixed(1)} suffix="%" tone="sky" testid="totals-att" />
                    <TotalsCard label="Avg Productivity %" value={ps.avg_productivity_pct !== null ? ps.avg_productivity_pct.toFixed(1) : "—"} suffix={ps.avg_productivity_pct !== null ? "%" : ""} tone="indigo" testid="totals-prod" />
                </aside>
            </div>

            {/* Privacy footer */}
            <div className="mt-6 px-3 py-2.5 rounded-md bg-violet-50 border border-violet-200 text-[11px] text-violet-800 flex items-start gap-2" data-testid="mh-privacy">
                <ShieldCheck size={14} weight="duotone" className="mt-0.5 shrink-0" />
                <div>
                    <strong>Privacy guaranteed:</strong> {data.privacy_note}
                </div>
            </div>
        </div>
    );
}

// ─── Reusable subcomponents ─────────────────────────────────────────

function SectionHeader({ icon: Icon, title, subtitle, tone, children }) {
    const tones = {
        rose: "text-rose-700",
        violet: "text-violet-700",
        indigo: "text-indigo-700",
        slate: "text-slate-700",
    };
    return (
        <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
                <Icon size={18} weight="duotone" className={tones[tone] || tones.slate} />
                <h2 className="text-sm font-bold text-slate-900">{title}</h2>
                {subtitle && <span className="text-[11px] text-slate-500">· {subtitle}</span>}
            </div>
            {children}
        </div>
    );
}

function AvailTile({ label, value, tone, icon: Icon, testid, onClick, active }) {
    const tones = {
        slate: "bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900 hover:border-emerald-300",
        amber: "bg-amber-50 border-amber-200 text-amber-900 hover:border-amber-300",
        rose: "bg-rose-50 border-rose-200 text-rose-900 hover:border-rose-300",
        sky: "bg-sky-50 border-sky-200 text-sky-900 hover:border-sky-300",
    };
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`rounded-md border p-2.5 text-left transition-all ${tones[tone] || tones.slate} ${active ? "ring-2 ring-offset-1 ring-violet-400 border-violet-400" : ""}`}
        >
            <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider font-semibold opacity-80">
                <Icon size={11} weight="fill" /> {label}
            </div>
            <div className="text-xl font-bold mt-0.5">{value ?? 0}</div>
        </button>
    );
}

function ExceptionBucket({ code, label, icon: Icon, tone, items }) {
    const [open, setOpen] = useState(false);
    const empty = items.length === 0;
    return (
        <div className={`rounded-md border p-2.5 ${tone}`} data-testid={`mh-exc-${code}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                disabled={empty}
                className="w-full flex items-center justify-between text-left"
            >
                <div className="flex items-center gap-1.5">
                    <Icon size={14} weight="duotone" />
                    <span className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</span>
                </div>
                <span className={`text-xl font-bold ${empty ? "opacity-30" : ""}`}>{items.length}</span>
            </button>
            {open && !empty && (
                <ul className="mt-2 space-y-1 text-xs">
                    {items.slice(0, 12).map((it) => (
                        <li key={`${code}-${it.employee_id}`} className="flex items-center justify-between gap-2 border-t border-current border-opacity-10 pt-1">
                            <Link to={`/employees/${it.employee_id}`} className="hover:underline truncate font-medium" data-testid={`mh-exc-${code}-row-${it.employee_id}`}>
                                {it.employee_name || it.employee_id}
                            </Link>
                            <span className="text-[10px] opacity-70 shrink-0">
                                {it.late_by_min !== undefined ? `+${it.late_by_min}m late` : ""}
                                {it.idle_min !== undefined ? `${it.idle_min}m idle` : ""}
                                {it.offline_min !== undefined ? `${it.offline_min}m off` : ""}
                            </span>
                        </li>
                    ))}
                    {items.length > 12 && (
                        <li className="text-[10px] opacity-60 italic">+{items.length - 12} more</li>
                    )}
                </ul>
            )}
        </div>
    );
}

function TeamRow({ row }) {
    const sourceKey = row.attendance_source || row.attendance_source_mode || "manual";
    const hasException = row.in_exception_buckets && row.in_exception_buckets.length > 0;

    return (
        <tr
            className={`border-t border-slate-100 hover:bg-slate-50 ${hasException ? "border-l-2 border-l-rose-400" : ""}`}
            data-testid={`mh-team-row-${row.employee_id}`}
        >
            <td className="px-3 py-2">
                <div className="text-sm font-semibold text-slate-900">{row.employee_name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5">
                    <span className="font-mono">{row.employee_code}</span>
                    {row.designation && <><span>·</span><span>{row.designation}</span></>}
                </div>
            </td>
            <td className="px-3 py-2">
                <Badge className={`text-xs ${STATUS_TONE[row.current_status] || STATUS_TONE.offline}`}>
                    {STATUS_LABEL[row.current_status] || row.current_status}
                </Badge>
                {row.current_status_secondary && (
                    <div className="text-[10px] text-slate-500 mt-0.5">{row.current_status_secondary}</div>
                )}
            </td>
            <td className="px-3 py-2 text-xs text-slate-600">{formatRelative(row.last_seen_at)}</td>
            <td className="px-3 py-2 text-right text-xs font-mono">
                <span className="text-emerald-700" title="Active">{Math.round(row.today_active_minutes || 0)}</span>
                <span className="text-slate-400 mx-0.5">/</span>
                <span className="text-amber-700" title="Idle">{Math.round(row.today_idle_minutes || 0)}</span>
                <span className="text-slate-400 mx-0.5">/</span>
                <span className="text-rose-700" title="Locked">{Math.round(row.today_locked_minutes || 0)}</span>
            </td>
            <td className="px-3 py-2 text-right text-xs">{formatMinutes(row.net_working_minutes)}</td>
            <td className="px-3 py-2">
                {row.attendance_status ? (
                    <Badge className="text-[10px] bg-slate-100 text-slate-700">{row.attendance_status}</Badge>
                ) : <span className="text-slate-300">—</span>}
                {row.needs_review && (
                    <Badge className="ml-1 text-[10px] bg-amber-100 text-amber-800">Needs Review</Badge>
                )}
            </td>
            <td className="px-3 py-2">
                <Badge className={`text-[10px] ${SOURCE_TONE[sourceKey] || "bg-slate-100"}`}>
                    {SOURCE_LABEL[sourceKey] || sourceKey}
                </Badge>
            </td>
            <td className="px-3 py-2 text-center">
                {row.productivity_pct !== null && row.productivity_band ? (
                    <Badge
                        className={`text-xs font-bold ${PROD_TONE[row.productivity_band]}`}
                        title="Indicative metric. Net working ÷ shift full-day. Policy-driven idle/locked treatment applied. Does NOT affect attendance or payroll."
                        data-testid={`mh-prod-${row.employee_id}`}
                    >
                        {row.productivity_pct.toFixed(1)}%
                    </Badge>
                ) : <span className="text-xs text-slate-300">N/A</span>}
            </td>
            <td className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                    <QuickIcon
                        to={`/workforce/desktop-activity?employee_id=${row.employee_id}`}
                        tip="Desktop Activity Timeline"
                        icon={MonitorPlay}
                        testid={`mh-q-act-${row.employee_id}`}
                    />
                    <QuickIcon
                        to={`/admin/attendance/snapshots?employee_id=${row.employee_id}&from_date=${today()}&to_date=${today()}`}
                        tip="Attendance Details"
                        icon={ChartBar}
                        testid={`mh-q-att-${row.employee_id}`}
                    />
                    <QuickIcon
                        to={`/regularization/requests?employee_id=${row.employee_id}`}
                        tip="Regularization"
                        icon={FileText}
                        testid={`mh-q-reg-${row.employee_id}`}
                    />
                    <QuickIcon
                        to={`/employees/${row.employee_id}`}
                        tip="Employee Profile"
                        icon={User}
                        testid={`mh-q-prof-${row.employee_id}`}
                    />
                </div>
            </td>
        </tr>
    );
}

function QuickIcon({ to, tip, icon: Icon, testid }) {
    return (
        <Link
            to={to}
            title={tip}
            data-testid={testid}
            className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"
        >
            <Icon size={14} weight="duotone" />
        </Link>
    );
}

function TotalsCard({ label, value, suffix, tone, testid }) {
    const tones = {
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    };
    return (
        <div className={`rounded-md border p-3 ${tones[tone]}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
            <div className="text-2xl font-bold mt-1">
                {value}{suffix && <span className="text-xs font-normal opacity-70 ml-0.5">{suffix}</span>}
            </div>
        </div>
    );
}



// ─────────────────────────────────────────────────────────────────────
// Iter 99.2 — Admin Picker View
// Rendered when the backend returns mode="admin_picker" — i.e. the
// caller is a workforce admin (primary super_admin / workforce:read /
// manager_dashboard:view_any) WITHOUT a personal direct-report team.
// ─────────────────────────────────────────────────────────────────────
function AdminPickerView({ data, onPick, secondsToRefresh, onRefresh }) {
    const [q, setQ] = useState("");
    const all = data?.managers || [];
    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return all;
        return all.filter(
            (m) =>
                (m.full_name || "").toLowerCase().includes(needle) ||
                (m.employee_code || "").toLowerCase().includes(needle) ||
                (m.department || "").toLowerCase().includes(needle) ||
                (m.designation || "").toLowerCase().includes(needle),
        );
    }, [all, q]);

    const totalReports = useMemo(
        () => all.reduce((acc, m) => acc + (m.direct_report_count || 0), 0),
        [all],
    );

    return (
        <div className="p-4 sm:p-6 max-w-[1920px] mx-auto" data-testid="mh-picker-root">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <Pulse size={22} weight="duotone" className="text-violet-700" />
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Workforce Health</h1>
                        <Badge className="bg-indigo-100 text-indigo-800 text-[10px]">Admin Picker</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-testid="mh-picker-header">
                        You can view any manager&apos;s team. Select a manager below to open their Team Health.
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="mh-refresh-countdown">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                        <span className="text-slate-400">·</span>
                        <span>refreshing in {secondsToRefresh}s</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={onRefresh} data-testid="mh-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
                    </Button>
                </div>
            </div>

            {/* Summary band */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="mh-picker-summary">
                <TotalsCard label="Managers" value={all.length} suffix="" tone="violet" testid="mh-picker-total-managers" />
                <TotalsCard label="Total Direct Reports" value={totalReports} suffix="" tone="indigo" testid="mh-picker-total-reports" />
                <TotalsCard label="Date" value={data?.scope?.date || "—"} suffix="" tone="slate" testid="mh-picker-date" />
                <TotalsCard label="Viewer" value={data?.scope?.viewer_email || "—"} suffix="" tone="sky" testid="mh-picker-viewer" />
            </div>

            {/* Search */}
            <div className="mb-3 flex items-center gap-2">
                <Input
                    placeholder="Filter by name, code, designation, department…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="max-w-md"
                    data-testid="mh-picker-search"
                />
                <div className="text-xs text-slate-500" data-testid="mh-picker-count">
                    {filtered.length} of {all.length} manager{all.length === 1 ? "" : "s"}
                </div>
            </div>

            {/* Picker grid */}
            {filtered.length === 0 ? (
                <div className="text-sm text-slate-500 p-6 text-center rounded-md border border-dashed border-slate-200" data-testid="mh-picker-empty">
                    No managers match your filter.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="mh-picker-grid">
                    {filtered.map((m) => (
                        <button
                            type="button"
                            key={m.employee_id}
                            onClick={() => onPick(m.employee_id)}
                            data-testid={`mh-picker-card-${m.employee_id}`}
                            className="text-left rounded-md border border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 bg-white p-3 transition-colors flex items-start gap-3"
                        >
                            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center">
                                <Users size={18} weight="duotone" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-slate-900 truncate">{m.full_name}</div>
                                <div className="text-[11px] text-slate-500 truncate">
                                    {m.employee_code}{m.designation ? ` · ${m.designation}` : ""}
                                </div>
                                {m.department && (
                                    <div className="text-[11px] text-slate-400 truncate">{m.department}</div>
                                )}
                                <div className="mt-1.5">
                                    <Badge className="bg-indigo-100 text-indigo-800 text-[10px]">
                                        {m.direct_report_count} direct report{m.direct_report_count === 1 ? "" : "s"}
                                    </Badge>
                                </div>
                            </div>
                            <ArrowSquareOut size={14} className="flex-shrink-0 text-slate-400 mt-1" />
                        </button>
                    ))}
                </div>
            )}

            {/* Privacy banner — same text as the team-view to keep the
                privacy invariant visible regardless of mode. */}
            <div className="mt-6 px-3 py-2.5 rounded-md bg-violet-50 border border-violet-200 text-[11px] text-violet-800 flex items-start gap-2" data-testid="mh-privacy">
                <ShieldCheck size={14} weight="duotone" className="flex-shrink-0 mt-0.5" />
                <span>{data?.privacy_note}</span>
            </div>
        </div>
    );
}
